import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { editLevels } from "../../../RTK/userlevels/EditeLevel";
import { X, Loader2, Upload } from "lucide-react";
import { fetchuserLevels } from "../../../RTK/userlevels/GetAll";

const EditLevel = ({ isOpen, onClose, levelData }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState(levelData || {});

  useEffect(() => {
    setFormData(levelData || {});
    setPreview(levelData?.icon || null);
  }, [levelData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        // Create a FileReader for preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreview(e.target.result);
        };
        reader.readAsDataURL(file);
  
        // Directly set the file object
        setFormData(prev => ({
          ...prev,
          icon: file
        }));
      } else {
        toast.error("Please select an image file");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Create FormData with proper encoding
      const formDataToSend = new FormData();
      
      // Handle basic fields first
      formDataToSend.append('name', formData.name);
      formDataToSend.append('level', formData.level);
      formDataToSend.append('min_monthly_spending', formData.min_monthly_spending);
      formDataToSend.append('profit_percentage', formData.profit_percentage);
      formDataToSend.append('hex_color', formData.hex_color);
  
      // Only append icon if it's a new file
      if (formData.icon instanceof File) {
        formDataToSend.append('icon', formData.icon, formData.icon.name);
      }
  
      // Update the editLevels API call
      await dispatch(editLevels({
        id: formData.id,
        updatedLevel: formDataToSend
      })).unwrap();
  
      await dispatch(fetchuserLevels()).unwrap();
      toast.success("Level updated successfully!");
      onClose();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error?.message || "Failed to update level");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[5000]">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-md p-6 z-[5500] shadow-2xl border border-gray-700 relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold text-white mb-6 text-right">
          تعديل المستوى
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col items-center bg-[#2a2a2a] p-4 rounded">
            <div className="img-input flex items-center gap-2 py-2">
              <img
                src={preview || "/api/placeholder/60/60"}
                alt="Preview"
                className="w-[60px] h-[60px] rounded-full border border-[#FEC111] object-cover"
              />
              <label
                htmlFor="file"
                className="cursor-pointer border-b border-[#FEC111] text-[#FEC111] font-semibold"
              >
                تغيير الصورة
              </label>
            </div>
            <input
              type="file"
              id="file"
              name="icon"
              className="hidden"
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 text-right mb-1">
                الحد الأدنى للإنفاق
              </label>
              <input
                type="number"
                step="0.01"
                name="min_monthly_spending"
                value={formData.min_monthly_spending || ""}
                onChange={handleChange}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                dir="rtl"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 text-right mb-1">
                اسم المستوى
              </label>
              <input
                type="text"
                name="name"
                value={formData.name || ""}
                onChange={handleChange}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                dir="rtl"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 text-right mb-1">
                المستوى
              </label>
              <input
                type="number"
                name="level"
                value={formData.level || ""}
                onChange={handleChange}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                dir="rtl"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 text-right mb-1">
                نسبة الربح (%)
              </label>
              <input
                type="number"
                step="0.1"
                name="profit_percentage"
                value={formData.profit_percentage || ""}
                onChange={handleChange}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                dir="rtl"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 text-right mb-1">
              اللون
            </label>
            <input
              type="color"
              name="hex_color"
              value={formData.hex_color || "#fec111"}
              onChange={handleChange}
              className="w-full h-10 bg-gray-700/50 border border-gray-600 rounded-lg p-1 cursor-pointer"
            />
          </div>

          <div className="flex justify-between gap-4 mt-8">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 focus:ring-2 focus:ring-gray-500 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className={`px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 focus:ring-2 focus:ring-blue-500 flex items-center gap-2 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              حفظ التغييرات
            </button>
          </div>
          
        </form>
      </div>
    </div>
  );
};

export default EditLevel;
